import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  FiDownload,
  FiPause,
  FiPlay,
  FiRepeat,
  FiSkipBack,
  FiSkipForward,
  FiSliders,
  FiTrash2,
  FiWifi,
  FiWifiOff,
} from 'react-icons/fi';
import { TbHeadphones } from 'react-icons/tb';
import TooltipIconButton from '../TooltipIconButton/TooltipIconButton';
import { formatAudioTime } from './audioFormatUtils';
import { canSeekAudio, getAudioSeekMax } from './audioSeekUtils';
import { resolveAudioOfflineFetchUrl } from './audioOfflineFetch';
import {
  deleteOfflineTrack,
  getOfflineTrack,
  hasOfflineTrack,
  saveOfflineTrack,
} from './audioOfflineStorage';
import { EQ_BANDS, EQ_PRESETS, isFlatEqGains, useWebAudioEqualizer } from './useWebAudioEqualizer';
import { shouldUseWebAudioEqualizer } from './audioEqualizerSupport';
import { useAudioMediaSession } from './useAudioMediaSession';
import { useAudioSleepTimer } from './useAudioSleepTimer';
import AudioPlayerSleepTimerControls from './AudioPlayerSleepTimerControls';
import AudioPlayerMobileToolsMenu from './AudioPlayerMobileToolsMenu';
import AudioPlayerHeadWindowActions from './AudioPlayerHeadWindowActions';
import { useCoarseMobileLayout } from './StorageBoxAudioStudioLayout';
import './AudioPlayer.css';

const SKIP_SECONDS = 10;
const LOOP_MODES = ['off', 'one'];

function readDocumentTheme() {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

function EqVisualizer({ analyserRef, active, ready }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!active || !ready) return undefined;
    const canvas = canvasRef.current;
    const analyser = analyserRef?.current;
    if (!canvas || !analyser) return undefined;

    const ctx = canvas.getContext('2d');
    const buffer = new Uint8Array(analyser.frequencyBinCount);

    const draw = () => {
      analyser.getByteFrequencyData(buffer);
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);
      const barWidth = width / buffer.length;
      for (let i = 0; i < buffer.length; i += 1) {
        const v = buffer[i] / 255;
        const barHeight = Math.max(4, v * height);
        const x = i * barWidth;
        const gradient = ctx.createLinearGradient(0, height, 0, height - barHeight);
        gradient.addColorStop(0, 'rgba(201, 134, 63, 0.35)');
        gradient.addColorStop(1, 'rgba(201, 134, 63, 0.95)');
        ctx.fillStyle = gradient;
        ctx.fillRect(x + 1, height - barHeight, Math.max(2, barWidth - 2), barHeight);
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, ready, analyserRef]);

  return (
    <canvas
      ref={canvasRef}
      className="audio-player__viz"
      width={280}
      height={48}
      aria-hidden
    />
  );
}

/**
 * Reusable HTML5 audio player with optional equalizer, offline cache, and light/dark themes.
 */
export default function AudioPlayer({
  src,
  title = 'Audio',
  subtitle = '',
  artist = '',
  artworkUrl = '',
  mimeType = '',
  offlineKey = '',
  allowOffline = true,
  allowEqualizer = true,
  allowStreamToggle = true,
  allowSleepTimer = true,
  theme = 'auto',
  compact = false,
  className = '',
  onFetchBlob,
  onError,
  onPlayStateChange,
  onQueueAdvance,
  playerApiRef = null,
  queueHasNextTrack = false,
  autoPlayRequest = 0,
  autoPlayOnSrcChange = false,
  equalizerGains = null,
  onEqualizerGainsChange = null,
  onEqualizerPanelChange = null,
  onWindowMinimize = null,
  onWindowClose = null,
}) {
  const audioRef = useRef(null);
  const progressScrubbingRef = useRef(false);
  const lastAutoPlayRequestRef = useRef(0);
  const pendingAutoPlayRequestRef = useRef(0);
  const resumePlaybackRef = useRef(false);
  const prevEffectiveSrcRef = useRef('');
  const titleId = useId();
  const mobileLayout = useCoarseMobileLayout();

  const resolvedTheme = theme === 'auto' ? readDocumentTheme() : theme;

  const [playbackMode, setPlaybackMode] = useState('stream');
  const [offlineReady, setOfflineReady] = useState(false);
  const [offlineBusy, setOfflineBusy] = useState(false);
  const [offlineUrl, setOfflineUrl] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [loopMode, setLoopMode] = useState('off');
  const [eqOpen, setEqOpen] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [eqGraphConnected, setEqGraphConnected] = useState(false);
  const [seekEnabled, setSeekEnabled] = useState(false);
  const [scrubTime, setScrubTime] = useState(null);

  const webAudioEqAllowed = shouldUseWebAudioEqualizer();
  const eqEnabled = allowEqualizer && webAudioEqAllowed && eqGraphConnected;
  const eqControlled = typeof onEqualizerGainsChange === 'function';
  const { ready: eqReady, gains, setBandGain, applyPreset, resetEq, analyserRef } =
    useWebAudioEqualizer(audioRef, eqEnabled, {
      controlledGains: eqControlled ? equalizerGains : undefined,
      onGainsChange: eqControlled ? onEqualizerGainsChange : undefined,
    });

  useEffect(() => {
    if (!webAudioEqAllowed) return;
    if (eqOpen || !isFlatEqGains(gains) || playing) {
      setEqGraphConnected(true);
    }
  }, [eqOpen, gains, webAudioEqAllowed, playing]);

  useEffect(() => {
    onEqualizerPanelChange?.(eqOpen);
  }, [eqOpen, onEqualizerPanelChange]);

  const effectiveSrc = playbackMode === 'offline' && offlineUrl ? offlineUrl : src || '';

  useEffect(() => {
    let cancelled = false;
    if (!offlineKey || !allowOffline) {
      setOfflineReady(false);
      return undefined;
    }
    hasOfflineTrack(offlineKey)
      .then((ok) => {
        if (!cancelled) setOfflineReady(ok);
      })
      .catch(() => {
        if (!cancelled) setOfflineReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [offlineKey, allowOffline, src]);

  useEffect(() => {
    let revoked = null;
    let cancelled = false;

    async function loadOffline() {
      if (playbackMode !== 'offline' || !offlineKey) {
        setOfflineUrl('');
        return;
      }
      try {
        const row = await getOfflineTrack(offlineKey);
        if (cancelled || !row?.blob) {
          setPlaybackMode('stream');
          setStatusMessage('Offline copy missing — switched to stream.');
          return;
        }
        const url = URL.createObjectURL(row.blob);
        revoked = url;
        setOfflineUrl(url);
        setStatusMessage('Playing from device storage (offline).');
      } catch (e) {
        if (!cancelled) {
          setPlaybackMode('stream');
          setStatusMessage(e.message || 'Could not load offline copy.');
        }
      }
    }

    loadOffline();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [playbackMode, offlineKey]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    audio.pause();
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setSeekEnabled(false);
    setLoadError('');
    setScrubTime(null);
  }, [effectiveSrc]);

  useEffect(() => {
    if (autoPlayRequest > lastAutoPlayRequestRef.current) {
      pendingAutoPlayRequestRef.current = autoPlayRequest;
      resumePlaybackRef.current = true;
    }
  }, [autoPlayRequest]);

  const runPlayAttempt = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !effectiveSrc) return undefined;

    const requestId = pendingAutoPlayRequestRef.current;
    const shouldPlay =
      resumePlaybackRef.current ||
      (autoPlayOnSrcChange && effectiveSrc !== prevEffectiveSrcRef.current);

    if (!shouldPlay) return undefined;
    if (
      requestId > 0 &&
      requestId <= lastAutoPlayRequestRef.current &&
      !resumePlaybackRef.current
    ) {
      return undefined;
    }

    const tryPlay = () => {
      if (requestId > 0) {
        lastAutoPlayRequestRef.current = requestId;
        pendingAutoPlayRequestRef.current = 0;
      }
      resumePlaybackRef.current = false;
      prevEffectiveSrcRef.current = effectiveSrc;
      audio
        .play()
        .then(() => setPlaying(true))
        .catch((e) => {
          setLoadError(e.message || 'Playback blocked');
          onError?.(e);
        });
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      tryPlay();
      return undefined;
    }

    audio.addEventListener('canplay', tryPlay, { once: true });
    return () => audio.removeEventListener('canplay', tryPlay);
  }, [autoPlayOnSrcChange, effectiveSrc, onError]);

  useEffect(() => runPlayAttempt(), [runPlayAttempt, autoPlayRequest]);

  useEffect(() => {
    onPlayStateChange?.(playing);
  }, [playing, onPlayStateChange]);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !effectiveSrc) return false;
    resumePlaybackRef.current = true;
    try {
      await audio.play();
      setPlaying(true);
      return true;
    } catch (e) {
      setLoadError(e.message || 'Playback blocked');
      onError?.(e);
      return false;
    }
  }, [effectiveSrc, onError]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !effectiveSrc) return;
    if (audio.paused) {
      try {
        await audio.play();
        setPlaying(true);
      } catch (e) {
        setLoadError(e.message || 'Playback blocked');
        onError?.(e);
      }
    } else {
      audio.pause();
      setPlaying(false);
    }
  }, [effectiveSrc, onError]);

  const stopForSleep = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
    }
    setPlaying(false);
    setStatusMessage('Sleep timer — playback stopped.');
  }, []);

  const sleepTimer = useAudioSleepTimer(allowSleepTimer ? stopForSleep : null);

  useEffect(() => {
    if (!playerApiRef) return undefined;
    playerApiRef.current = {
      togglePlay,
      play,
      playing,
      sleepTimerActive: sleepTimer.isActive,
      sleepTimerRemainingLabel: sleepTimer.remainingLabel,
      clearSleepTimer: sleepTimer.clearTimer,
      toggleEqualizer: () => setEqOpen((o) => !o),
      openEqualizer: () => setEqOpen(true),
      equalizerOpen: eqOpen,
    };
    return () => {
      playerApiRef.current = null;
    };
  }, [
    playerApiRef,
    togglePlay,
    play,
    playing,
    sleepTimer.isActive,
    sleepTimer.remainingLabel,
    sleepTimer.clearTimer,
    eqOpen,
  ]);

  const seekToTime = useCallback((time) => {
    const audio = audioRef.current;
    if (!audio) return;
    const max = getAudioSeekMax(audio);
    const cap =
      Number.isFinite(max) && max > 0 && max !== Infinity
        ? max
        : Number.isFinite(duration) && duration > 0
          ? duration
          : 0;
    if (cap <= 0) return;
    const next = Math.min(cap, Math.max(0, time));
    audio.currentTime = next;
    setCurrentTime(next);
    setScrubTime(next);
  }, [duration]);

  const skip = useCallback((delta) => {
    const audio = audioRef.current;
    if (!audio) return;
    const max = getAudioSeekMax(audio);
    if (!canSeekAudio(audio)) {
      setStatusMessage('Skipping isn’t available for this live stream.');
      return;
    }
    const cap = Number.isFinite(max) && max > 0 ? max : audio.currentTime + Math.abs(delta);
    const next = Math.min(cap, Math.max(0, audio.currentTime + delta));
    audio.currentTime = next;
    setCurrentTime(next);
  }, []);

  const syncSeekState = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const max = getAudioSeekMax(audio);
    if (Number.isFinite(max) && max > 0 && max !== Infinity) {
      setDuration(max);
      setSeekEnabled(true);
      return;
    }
    if (canSeekAudio(audio)) {
      setSeekEnabled(true);
      return;
    }
    const metaDur = audio.duration;
    if (Number.isFinite(metaDur) && metaDur > 0 && metaDur !== Infinity) {
      setDuration(metaDur);
      setSeekEnabled(true);
    }
  }, []);

  const handleMediaSessionTransport = useCallback(
    (action) => {
      const audio = audioRef.current;
      if (!audio || !effectiveSrc) return;
      if (action === 'play' && audio.paused) {
        audio.play().then(() => setPlaying(true)).catch((e) => {
          setLoadError(e.message || 'Playback blocked');
          onError?.(e);
        });
      } else if (action === 'pause' && !audio.paused) {
        audio.pause();
        setPlaying(false);
      }
    },
    [effectiveSrc, onError]
  );

  useAudioMediaSession({
    title,
    subtitle,
    artist,
    artworkUrl,
    playing,
    audioRef,
    onTogglePlay: handleMediaSessionTransport,
    onSkipBackward: () => skip(-SKIP_SECONDS),
    onSkipForward: () => skip(SKIP_SECONDS),
  });

  const cycleLoop = useCallback(() => {
    setLoopMode((prev) => {
      const idx = LOOP_MODES.indexOf(prev);
      return LOOP_MODES[(idx + 1) % LOOP_MODES.length];
    });
  }, []);

  const handleDownloadOffline = useCallback(async () => {
    if (!allowOffline || !offlineKey) return;
    setOfflineBusy(true);
    setStatusMessage('');
    try {
      let blob;
      if (onFetchBlob) {
        blob = await onFetchBlob();
      } else if (src) {
        const fetchUrl = resolveAudioOfflineFetchUrl(src);
        const res = await fetch(fetchUrl, { credentials: 'include' });
        if (!res.ok) {
          let message = 'Download failed';
          try {
            const data = await res.json();
            if (data?.message) message = data.message;
          } catch {
            /* binary or empty body */
          }
          throw new Error(message);
        }
        blob = await res.blob();
      } else {
        throw new Error('Nothing to download');
      }
      await saveOfflineTrack(offlineKey, {
        blob,
        title,
        mimeType: mimeType || blob.type,
        originalUrl: src,
      });
      setOfflineReady(true);
      setStatusMessage('Saved on this device for offline playback.');
    } catch (e) {
      setStatusMessage(e.message || 'Could not save offline');
      onError?.(e);
    } finally {
      setOfflineBusy(false);
    }
  }, [allowOffline, offlineKey, onFetchBlob, src, title, mimeType, onError]);

  const handleDeleteOffline = useCallback(async () => {
    if (!offlineKey) return;
    setOfflineBusy(true);
    try {
      await deleteOfflineTrack(offlineKey);
      setOfflineReady(false);
      if (playbackMode === 'offline') setPlaybackMode('stream');
      setStatusMessage('Removed offline copy from this device.');
    } catch (e) {
      setStatusMessage(e.message || 'Could not delete offline copy');
    } finally {
      setOfflineBusy(false);
    }
  }, [offlineKey, playbackMode]);

  const loopLabel = useMemo(() => {
    if (loopMode === 'one') return 'Loop on';
    return 'Loop off';
  }, [loopMode]);

  const progressValue =
    duration > 0 ? Math.min(scrubTime ?? currentTime, duration) : scrubTime ?? currentTime ?? 0;

  const rootClass = [
    'audio-player',
    `audio-player--${resolvedTheme}`,
    compact ? 'audio-player--compact' : '',
    mobileLayout ? 'audio-player--mobile-studio' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={rootClass} aria-labelledby={titleId}>
      <div className="audio-player__head-shell">
        {(mobileLayout || onWindowMinimize || onWindowClose) ? (
          <div className="audio-player__head-actions">
            {!mobileLayout ? (
              <AudioPlayerHeadWindowActions
                onMinimize={onWindowMinimize}
                onClose={onWindowClose}
              />
            ) : null}
            {mobileLayout ? (
              <AudioPlayerMobileToolsMenu
                allowEqualizer={allowEqualizer}
                equalizerOpen={eqOpen}
                onToggleEqualizer={() => setEqOpen((o) => !o)}
                allowSleepTimer={allowSleepTimer}
                sleepTimer={sleepTimer}
                allowOffline={allowOffline}
                offlineKey={offlineKey}
                offlineReady={offlineReady}
                offlineBusy={offlineBusy}
                canDownload={Boolean(src)}
                onDownloadOffline={handleDownloadOffline}
                onDeleteOffline={handleDeleteOffline}
                onWindowMinimize={onWindowMinimize}
                onWindowClose={onWindowClose}
              />
            ) : null}
          </div>
        ) : null}
        <header className="audio-player__head">
          {artworkUrl ? (
            <img className="audio-player__artwork" src={artworkUrl} alt="" width={56} height={56} />
          ) : (
            <span className="audio-player__brand-icon" aria-hidden>
              <TbHeadphones />
            </span>
          )}
          <div className="audio-player__titles">
            <h4 id={titleId} className="audio-player__title" title={title}>
              {title}
            </h4>
            {artist ? <p className="audio-player__artist">{artist}</p> : null}
            {subtitle ? <p className="audio-player__subtitle">{subtitle}</p> : null}
          </div>
        </header>
      </div>

      {allowOffline && offlineKey ? (
        <div className="audio-player__offline-actions audio-player__offline-actions--desktop">
          <button
            type="button"
            className="audio-player__secondary"
            onClick={handleDownloadOffline}
            disabled={offlineBusy || !src}
          >
            <FiDownload aria-hidden />{' '}
            <span className="audio-player__offline-label audio-player__offline-label--long">
              {offlineReady ? 'Update offline copy' : 'Download for offline'}
            </span>
            <span className="audio-player__offline-label audio-player__offline-label--short">
              {offlineReady ? 'Update' : 'Save offline'}
            </span>
          </button>
          {offlineReady ? (
            <button
              type="button"
              className="audio-player__secondary audio-player__secondary--danger"
              onClick={handleDeleteOffline}
              disabled={offlineBusy}
            >
              <FiTrash2 aria-hidden />{' '}
              <span className="audio-player__offline-label audio-player__offline-label--long">
                Delete offline copy
              </span>
              <span className="audio-player__offline-label audio-player__offline-label--short">Remove</span>
            </button>
          ) : null}
        </div>
      ) : null}

      {allowStreamToggle && allowOffline && offlineKey ? (
        <div className="audio-player__mode" role="group" aria-label="Playback source">
          <button
            type="button"
            className={`audio-player__mode-btn${playbackMode === 'stream' ? ' audio-player__mode-btn--active' : ''}`}
            onClick={() => setPlaybackMode('stream')}
            disabled={!src}
          >
            <FiWifi aria-hidden /> Stream
          </button>
          <button
            type="button"
            className={`audio-player__mode-btn${playbackMode === 'offline' ? ' audio-player__mode-btn--active' : ''}`}
            onClick={() => setPlaybackMode('offline')}
            disabled={!offlineReady}
          >
            <FiWifiOff aria-hidden /> Offline
          </button>
        </div>
      ) : null}

      {eqEnabled && eqReady ? (
        <EqVisualizer analyserRef={analyserRef} active={playing} ready={eqReady} />
      ) : (
        <div className="audio-player__viz audio-player__viz--placeholder" aria-hidden />
      )}

      <audio
        ref={audioRef}
        className="audio-player__native"
        src={effectiveSrc || undefined}
        preload="metadata"
        playsInline
        loop={loopMode === 'one'}
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration || 0);
          syncSeekState();
        }}
        onTimeUpdate={(e) => {
          if (progressScrubbingRef.current) return;
          setCurrentTime(e.currentTarget.currentTime || 0);
        }}
        onProgress={() => syncSeekState()}
        onDurationChange={() => syncSeekState()}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          if (loopMode === 'one') {
            const audio = audioRef.current;
            if (audio) {
              audio.currentTime = 0;
              audio.play().catch(() => {});
            }
            return;
          }
          const hasNext =
            typeof queueHasNextTrack === 'function' ? queueHasNextTrack() : Boolean(queueHasNextTrack);
          const sleepAction = sleepTimer.handleTrackEnded(hasNext);
          if (sleepAction === 'handled') return;
          if (!onQueueAdvance) return;
          resumePlaybackRef.current = true;
          if (sleepAction === 'advance') {
            onQueueAdvance();
            return;
          }
          onQueueAdvance();
        }}
        onError={(e) => {
          const code = e.currentTarget.error?.code;
          const msg =
            code === 4
              ? 'This stream could not be loaded (blocked URL, unsupported format, or network error).'
              : 'Unable to play this audio format or file.';
          setLoadError(msg);
          onError?.(new Error(msg));
        }}
      />

      <div className="audio-player__timeline">
        <span className="audio-player__time">{formatAudioTime(currentTime)}</span>
        <div className="audio-player__progress-wrap">
          <input
            type="range"
            className="audio-player__progress-range"
            min={0}
            max={duration > 0 ? duration : 1}
            step={0.05}
            value={progressValue}
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={duration > 0 ? duration : 0}
            aria-valuenow={scrubTime ?? currentTime}
            aria-valuetext={`${formatAudioTime(scrubTime ?? currentTime)} of ${formatAudioTime(duration)}`}
            disabled={!effectiveSrc || duration <= 0}
            onPointerDown={(e) => {
              progressScrubbingRef.current = true;
              if (e.currentTarget.setPointerCapture) {
                e.currentTarget.setPointerCapture(e.pointerId);
              }
            }}
            onPointerUp={(e) => {
              progressScrubbingRef.current = false;
              setScrubTime(null);
              if (e.currentTarget.releasePointerCapture) {
                try {
                  e.currentTarget.releasePointerCapture(e.pointerId);
                } catch {
                  /* not captured */
                }
              }
            }}
            onPointerCancel={(e) => {
              progressScrubbingRef.current = false;
              setScrubTime(null);
              if (e.currentTarget.releasePointerCapture) {
                try {
                  e.currentTarget.releasePointerCapture(e.pointerId);
                } catch {
                  /* not captured */
                }
              }
            }}
            onInput={(e) => {
              progressScrubbingRef.current = true;
              seekToTime(Number(e.target.value));
            }}
            onChange={(e) => {
              seekToTime(Number(e.target.value));
              progressScrubbingRef.current = false;
              setScrubTime(null);
            }}
          />
        </div>
        <span className="audio-player__time">{formatAudioTime(duration)}</span>
      </div>

      <div className="audio-player__controls">
        <TooltipIconButton
          label={`Back ${SKIP_SECONDS}s`}
          icon={<FiSkipBack />}
          onClick={() => skip(-SKIP_SECONDS)}
          disabled={!effectiveSrc || !seekEnabled}
        />
        <button
          type="button"
          className="audio-player__play"
          aria-label={playing ? 'Pause' : 'Play'}
          onClick={togglePlay}
          disabled={!effectiveSrc}
        >
          {playing ? <FiPause aria-hidden /> : <FiPlay aria-hidden />}
        </button>
        <TooltipIconButton
          label={`Forward ${SKIP_SECONDS}s`}
          icon={<FiSkipForward />}
          onClick={() => skip(SKIP_SECONDS)}
          disabled={!effectiveSrc || !seekEnabled}
        />
        <TooltipIconButton
          label={loopLabel}
          className={loopMode !== 'off' ? 'audio-player__loop--active' : ''}
          icon={<FiRepeat />}
          onClick={cycleLoop}
          disabled={!effectiveSrc}
        />
        <label className="audio-player__volume">
          <span className="audio-player__volume-label">Vol</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            aria-label="Volume"
            onChange={(e) => {
              const v = Number(e.target.value);
              setVolume(v);
              if (audioRef.current) audioRef.current.volume = v;
            }}
          />
        </label>
      </div>

      {allowSleepTimer ? (
        <AudioPlayerSleepTimerControls
          sleepTimer={sleepTimer}
          className="audio-player__sleep-wrap--desktop"
        />
      ) : null}

      {allowEqualizer ? (
        <div className="audio-player__eq">
          <button
            type="button"
            className={`audio-player__eq-toggle${eqOpen ? ' audio-player__eq-toggle--open' : ''}${
              !isFlatEqGains(gains) ? ' audio-player__eq-toggle--active' : ''
            }`}
            aria-expanded={eqOpen}
            onClick={() => setEqOpen((o) => !o)}
          >
            <FiSliders className="audio-player__eq-toggle-icon" aria-hidden />
            <span>Equalizer</span>
            <span className="audio-player__eq-toggle-chevron" aria-hidden>
              {eqOpen ? '▾' : '▸'}
            </span>
          </button>
          {eqOpen ? (
            <div className="audio-player__eq-panel">
              {!webAudioEqAllowed ? (
                <p className="audio-player__eq-mobile-hint" role="note">
                  Choose a preset or adjust bands below — settings are saved for this playlist. Tone
                  shaping is not applied during playback on phones and tablets so audio keeps playing
                  when the screen is locked.
                </p>
              ) : null}
              <div className="audio-player__eq-presets" role="group" aria-label="EQ presets">
                {Object.keys(EQ_PRESETS).map((presetId) => (
                  <button
                    key={presetId}
                    type="button"
                    className="audio-player__eq-preset"
                    onClick={() => applyPreset(presetId)}
                  >
                    {presetId}
                  </button>
                ))}
                <button
                  type="button"
                  className="audio-player__eq-preset"
                  onClick={() => resetEq()}
                >
                  reset
                </button>
              </div>
              <div className="audio-player__eq-sliders">
                {EQ_BANDS.map((band) => (
                  <label key={band.id} className="audio-player__eq-band">
                    <span>{band.label}</span>
                    <input
                      type="range"
                      min="-12"
                      max="12"
                      step="0.5"
                      value={gains[band.id] ?? 0}
                      disabled={webAudioEqAllowed && !eqReady}
                      onChange={(e) => setBandGain(band.id, Number(e.target.value))}
                    />
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {loadError ? (
        <p className="audio-player__message audio-player__message--error" role="alert">
          {loadError}
        </p>
      ) : null}
      {statusMessage ? (
        <p className="audio-player__message" role="status">
          {statusMessage}
        </p>
      ) : null}
    </section>
  );
}
